/**
 * Maine State-Specific Diagnostic Questions
 * All questions reference actual ME regulations, forms, and requirements
 * 
 * PRIMARY REGULATION: 10-144 CMR Chapter 119 - Regulations Governing the 
 * Licensing and Functioning of Home Health Care Services
 * 
 * STATUTORY AUTHORITY: 22 MRSA Chapter 419 - Home Health Services
 * 
 * Regulatory Body: Maine Department of Health and Human Services (DHHS)
 * Division of Licensing and Certification (DLC)
 * 
 * STRUCTURE OF CHAPTER 119:
 * - Chapter 1: General Definitions
 * - Chapter 2: Licensing Requirements
 * - Chapter 3: Governance
 * - Chapter 4: Administration
 * - Chapter 5: Personnel
 * - Chapter 6: Health Care Paraprofessional Requirements
 * - Chapter 7: Services (Plan of Care, Patient Rights, Records, Supervision)
 * - Chapter 8: Quality Assurance
 * 
 * KEY RELATED STATUTES:
 * - 22 MRSA §1724: Criminal background checks
 * - 22 MRSA §1812-G: Maine Registry of CNAs and Direct Care Workers
 * - 22 MRSA Chapter 1691: Maine Background Check Center Act
 * - 22 MRSA §3477: Mandatory reporting (Adult Protective Services)
 * 
 * KEY CONTACTS:
 * - DHHS-DLC Home Health Hotline: (207) 287-9302 (toll-free: 1-800-621-8222)
 * - Background Check Center: backgroundcheck.maine.gov
 * - APS Hotline: 1-800-624-8404
 * 
 * Last Verified: December 2025
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MAINE_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'ME-A1',
    itemId: 'A1',
    stateCode: 'ME',
    title: 'DHHS Home Health Agency License Status',
    description: 'Maine requires all home health agencies to be licensed by DHHS under 10-144 CMR Chapter 119',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'Licensure',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Maine DHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses are now valid for 2 years. Medicare-certified agencies are deemed to meet state requirements.',
      },
      {
        id: 'q2',
        question: 'What is your DHHS license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DHHS license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Maine DHHS Home Health Agency license is current and active per 10-144 CMR Chapter 119.',
      atRisk: 'Your DHHS license expires within 90 days. Maine requires renewal applications to be submitted before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Maine without a valid DHHS license violates 10-144 CMR Chapter 119 and 22 MRSA Chapter 419. Contact DHHS-DLC immediately at (207) 287-9300.',
      unknown: 'Verify your DHHS license status by calling (207) 287-9300.',
    },
    stateNotes: [
      'DHHS licenses are now valid for 2 years (recently updated from 1 year)',
      'Medicare-certified agencies are deemed to meet state licensure requirements per 10-144 CMR Chapter 119, Section 1.12',
      'Maine follows federal conditions of participation for certified agencies (42 CFR Part 484)',
      'Statutory authority: 22 MRSA Chapter 419 - Home Health Services',
    ],
  },

  {
    id: 'ME-A2',
    itemId: 'A2',
    stateCode: 'ME',
    title: 'Ownership and Administrator Information Current',
    description: 'Ownership and administrator information must be current with DHHS per 22 MRSA §1723',
    severity: 'medium',
    regulationCode: '22-1723',
    regulationName: 'Change Notification',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Is your ownership and administrator information current with DHHS?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, all information is current' },
          { value: 'pending', label: 'Changes pending notification to DHHS' },
          { value: 'outdated', label: 'No, information is outdated' },
          { value: 'unsure', label: 'Not sure if current' },
        ],
        required: true,
        helpText: 'Changes in ownership, administrator, or Program Director require a change notice with applicable fee per 22 MRSA §1723',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'current', result: 'COMPLIANT' },
      { condition: 'includes', value: ['pending', 'unsure'], result: 'AT_RISK' },
      { condition: 'equals', value: 'outdated', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your ownership and administrator information is current with Maine DHHS.',
      atRisk: 'Changes in ownership, administrator, or Program Director must be reported to DHHS with the Home Health Service Provider Change Notice form and applicable fee. Submit notification immediately if changes have occurred.',
      nonCompliant: 'Your ownership and administrator information is not current with DHHS. Contact DHHS-DLC at (207) 287-9300 to update your records immediately.',
      unknown: 'Verify your ownership and administrator information is current with DHHS.',
    },
    stateNotes: [
      'Use the online Home Health Service Provider Change Notice form for state-licensed-only agencies',
      'Medicare-certified agencies must also complete CMS-required forms',
      'Changes requiring new licensure must include applicable fee per 22 MRSA §1723',
    ],
  },

  {
    id: 'ME-A3',
    itemId: 'A3',
    stateCode: 'ME',
    title: 'Insurance Requirements',
    description: 'Maine requires specific insurance coverage for home health agencies',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'Insurance',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl_1m', label: 'General liability - $1,000,000 per occurrence' },
          { value: 'gl_3m', label: 'General liability - $3,000,000 aggregate' },
          { value: 'prof', label: 'Professional liability insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance (39-A MRSA)' },
          { value: 'bond', label: 'Fidelity bond coverage' },
        ],
        required: true,
        helpText: 'Coverage requirements per 10-144 CMR Chapter 119 and 39-A MRSA (Workers\' Compensation)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Maine requirements.',
      atRisk: 'Your insurance may not meet all Maine requirements. Review coverage requirements and consult with DHHS-DLC.',
      nonCompliant: 'Your insurance coverage does not meet Maine requirements. Contact DHHS-DLC at (207) 287-9300 for specific coverage requirements.',
      unknown: 'Verify your insurance meets requirements.',
    },
    stateNotes: [
      'Workers\' compensation required under 39-A MRSA (Maine Workers\' Compensation Act)',
      'Contact Maine Workers\' Compensation Board at (207) 287-3751 for WC questions',
    ],
  },

  {
    id: 'ME-A4',
    itemId: 'A4',
    stateCode: 'ME',
    title: 'Written Policies and Procedures',
    description: 'Maine requires specific written policies per 10-144 CMR Chapter 119',
    severity: 'medium',
    regulationCode: '10-144-119',
    regulationName: 'Policies',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Maine-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Patient rights and responsibilities (Section 7.G.4)' },
          { value: 'service_agreement', label: 'Written service agreements' },
          { value: 'admission_criteria', label: 'Admission, transfer, and discharge criteria (Section 7.G)' },
          { value: 'supervision', label: 'Clinical supervision requirements (Section 7.D)' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Criminal background check procedures (22 MRSA §1724)' },
          { value: 'training', label: 'Staff training requirements' },
          { value: 'infection_control', label: 'Infection control policies' },
          { value: 'abuse_reporting', label: 'Abuse and neglect reporting (22 MRSA §3477)' },
          { value: 'emergency', label: 'Emergency preparedness' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Maine 10-144 CMR Chapter 119 requirements.',
      atRisk: 'Your policy manual is missing required sections. DHHS surveyors will review your policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per 10-144 CMR Chapter 119. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against 10-144 CMR Chapter 119 requirements.',
    },
    stateNotes: [
      '10-144 CMR Chapter 119 requires written policies and procedures',
      'Policies must be reviewed and available to staff',
      'Medicare-certified agencies must also meet 42 CFR 484 requirements',
      'DHHS-DLC reviews policies during licensure surveys',
    ],
  },

  {
    id: 'ME-A5',
    itemId: 'A5',
    stateCode: 'ME',
    title: 'Incident Reporting Process',
    description: 'Maine requires documented incident reporting procedures per 10-144 CMR Chapter 119 and Chapter 114',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'Incident Reporting',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented and followed consistently' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which types of incidents do you report? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'abuse', label: 'Suspected abuse, neglect, or exploitation' },
          { value: 'injury', label: 'Patient injuries during service' },
          { value: 'death', label: 'Patient deaths (sentinel events per 10-144 CMR Chapter 114)' },
          { value: 'medication', label: 'Medication errors' },
          { value: 'complaints', label: 'Serious complaints' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting process is documented and followed per 10-144 CMR Chapter 119.',
      atRisk: 'Incident reporting process exists but may not be fully documented. Formalize your procedures.',
      nonCompliant: 'No incident reporting process is in place. Implement a documented incident reporting process immediately.',
      unknown: 'Verify your incident reporting procedures meet requirements.',
    },
    stateNotes: [
      'Sentinel events must be reported per 10-144 CMR Chapter 114 (Rules Governing the Reporting of Sentinel Events)',
      'Abuse/neglect must be reported to Adult Protective Services: 1-800-624-8404',
    ],
  },

  {
    id: 'ME-A6',
    itemId: 'A6',
    stateCode: 'ME',
    title: 'Emergency Preparedness Plan',
    description: 'Maine requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: '10-144-119',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan in place' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
        helpText: 'Emergency plans should address winter storms, power outages, and service continuity',
      },
      {
        id: 'q2',
        question: 'Does your emergency plan include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_contact', label: 'Patient emergency contact procedures' },
          { value: 'staff_notification', label: 'Staff notification procedures' },
          { value: 'winter_weather', label: 'Winter storm/nor\'easter procedures' },
          { value: 'power_outage', label: 'Power outage protocols' },
          { value: 'continuity', label: 'Service continuity plans' },
          { value: 'communication', label: 'Communication backup plans' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and documented.',
      atRisk: 'Emergency preparedness plan is incomplete. Develop a comprehensive plan addressing Maine-specific weather hazards.',
      nonCompliant: 'No emergency preparedness plan is in place. Develop a comprehensive emergency plan immediately.',
      unknown: 'Review your emergency preparedness procedures for completeness.',
    },
    stateNotes: [
      'Maine experiences severe winter weather requiring specific preparedness',
      'Consider nor\'easters, ice storms, power outages, and rural access issues',
      'Medicare-certified agencies must also comply with 42 CFR §484.22 (Emergency Preparedness)',
    ],
  },

  {
    id: 'ME-A7',
    itemId: 'A7',
    stateCode: 'ME',
    title: 'Complaint and Grievance Handling Process',
    description: 'Maine requires documented complaint handling procedures per 10-144 CMR Chapter 119, Section 7.G.4',
    severity: 'medium',
    regulationCode: '10-144-119.7.G.4',
    regulationName: 'Complaints',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented process for handling patient complaints and grievances?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented process with tracking' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No complaint handling process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your complaint process include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'written', label: 'Written complaint procedures provided to patients' },
          { value: 'hotline', label: 'Home Health Care Services Hotline number provided' },
          { value: 'ombudsman', label: 'Long Term Care Ombudsman Program information' },
          { value: 'tracking', label: 'Complaint tracking and documentation' },
          { value: 'response', label: 'Defined response timeframes' },
          { value: 'resolution', label: 'Resolution documentation' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint and grievance handling process is documented and includes all required elements.',
      atRisk: 'Complaint handling process exists but may not be fully documented. Ensure patients receive required information.',
      nonCompliant: 'No complaint handling process is in place. Implement a documented complaint process immediately.',
      unknown: 'Verify your complaint procedures meet requirements.',
    },
    stateNotes: [
      'Per Section 7.G.4, patients must be advised of the Home Health Care Services Hotline',
      'Patients must be informed they can file complaints with DLC, Bureau of Elder and Adult Services, or Long Term Care Ombudsman',
      'Provider must investigate and document complaints per Section 7.G.4.b.5',
    ],
  },

  {
    id: 'ME-A8',
    itemId: 'A8',
    stateCode: 'ME',
    title: 'Quality Assurance Reviews Documented',
    description: 'Maine recommends documented quality assurance processes',
    severity: 'medium',
    regulationCode: '10-144-119',
    regulationName: 'Quality Assurance',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct and document quality assurance reviews?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented reviews (at least quarterly)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality assurance reviews' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What does your quality assurance process include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'file_audits', label: 'Personnel file audits' },
          { value: 'patient_satisfaction', label: 'Patient satisfaction surveys' },
          { value: 'supervision', label: 'Staff supervision and performance reviews' },
          { value: 'compliance_checks', label: 'Regulatory compliance checks' },
          { value: 'training_verification', label: 'Training completion verification' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'regular' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'occasional' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance reviews are conducted regularly and documented.',
      atRisk: 'Quality assurance reviews are not conducted consistently or fully documented. Implement regular documented reviews.',
      nonCompliant: 'No quality assurance reviews are conducted. Implement a regular QA process.',
      unknown: 'Consider implementing a documented quality assurance program.',
    },
    stateNotes: [
      '10-144 CMR Chapter 119, Chapter 8 addresses quality assurance',
      'Medicare-certified agencies must meet QAPI requirements per 42 CFR 484.65',
      'Document all QA activities and corrective actions',
      'Include patient satisfaction and clinical outcome reviews',
    ],
  },

  {
    id: 'ME-A9',
    itemId: 'A9',
    stateCode: 'ME',
    title: 'Client Service Agreements Current and Signed',
    description: 'Maine requires written service agreements with all patients per 10-144 CMR Chapter 119, Section 7.G.4.e',
    severity: 'medium',
    regulationCode: '10-144-119.7.G.4.e',
    regulationName: 'Service Agreements',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current, signed service agreements with ALL patients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients have signed current agreements' },
          { value: 'most', label: 'Most patients have signed agreements' },
          { value: 'some', label: 'Some agreements are missing or outdated' },
          { value: 'no', label: 'No formal service agreements in place' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your service agreements include all Maine-required disclosures?',
        inputType: 'checklist',
        options: [
          { value: 'rates', label: 'Rates, fees, and expected payment sources' },
          { value: 'services', label: 'Description of services to be provided' },
          { value: 'rights', label: 'Patient rights and responsibilities' },
          { value: 'cancellation', label: 'Cancellation and termination policies' },
          { value: 'complaint', label: 'Complaint procedures and hotline information' },
          { value: 'advance_directive', label: 'Advance directive policies' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient service agreements are current and properly executed per 10-144 CMR Chapter 119.',
      atRisk: 'Some patient service agreements may be missing required disclosures or signatures. Review all agreements.',
      nonCompliant: 'Patient service agreements are missing or incomplete. Obtain signed agreements from all patients immediately.',
      unknown: 'Verify all patient service agreements are current and contain required disclosures.',
    },
    stateNotes: [
      'Per Section 7.G.4.e, patients must be advised orally and in writing of payment expectations before care begins',
      'Must disclose Medicare/Medicaid coverage, charges for uncovered services, and patient charges',
      'Changes in payment information must be communicated within 30 days per Section 7.G.4.e.2',
    ],
  },

  {
    id: 'ME-A10',
    itemId: 'A10',
    stateCode: 'ME',
    title: 'Record Retention',
    description: 'Maine requires minimum 5-year record retention per 10-144 CMR Chapter 119, Section 7.F.2',
    severity: 'medium',
    regulationCode: '10-144-119.7.F.2',
    regulationName: 'Record Retention',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer' },
          { value: '5years', label: '5 years (meets ME minimum requirement)' },
          { value: '4years', label: '4 years' },
          { value: 'less4', label: 'Less than 4 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Maine 10-144 CMR Chapter 119, Section 7.F.2 requires retention for at least 5 years beyond last date of service',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['6plus', '5years'], result: 'COMPLIANT' },
      { condition: 'equals', value: '4years', result: 'AT_RISK' },
      { condition: 'includes', value: ['less4', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Maine\'s 5-year minimum requirement under 10-144 CMR Chapter 119, Section 7.F.2.',
      atRisk: 'Maine requires minimum 5-year record retention. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Maine\'s 5-year minimum requirement. Immediately update your retention policy.',
      unknown: 'Review record retention requirements under 10-144 CMR Chapter 119, Section 7.F.2.',
    },
    stateNotes: [
      'Records must be retained at main business or subunit offices',
      'Must also conform to applicable state and federal laws which may require longer retention',
      'Medicare-certified agencies must also meet federal retention requirements',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'ME-B1',
    itemId: 'B1',
    stateCode: 'ME',
    title: 'Personnel File Requirements',
    description: 'Maine requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'Personnel',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application with work history' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'license', label: 'Professional license verification' },
          { value: 'background', label: 'Criminal background check clearance (22 MRSA §1724)' },
          { value: 'references', label: 'Reference verification (minimum 2)' },
          { value: 'health', label: 'Health clearance/physical exam' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'orientation', label: 'Orientation and training documentation' },
          { value: 'job_description', label: 'Signed job description' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Maine-required documentation per 10-144 CMR Chapter 119.',
      atRisk: 'Some personnel files are missing required documents. Maine requires complete documentation before staff provide care.',
      nonCompliant: 'Personnel files are missing critical documentation. DHHS surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against requirements.',
    },
    stateNotes: [
      '10-144 CMR Chapter 119, Section 5 covers personnel requirements',
      'Background check clearance required before patient contact per 22 MRSA 1724',
      'All documentation must be in place BEFORE patient contact',
      'DHHS surveyors review personnel files during inspections',
    ],
  },

  {
    id: 'ME-B2',
    itemId: 'B2',
    stateCode: 'ME',
    title: 'Government-Issued Photo ID on File',
    description: 'Maine requires valid government-issued photo ID for all employees',
    severity: 'medium',
    regulationCode: '10-144-119',
    regulationName: 'Personnel',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your employees have a current, valid government-issued photo ID on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have valid photo ID on file' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
        helpText: 'Acceptable IDs include driver\'s license, state ID, or passport',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees have valid government-issued photo ID on file.',
      atRisk: 'Some employees are missing government-issued photo ID. Obtain copies of valid ID for all personnel files immediately.',
      nonCompliant: 'Personnel files are missing required government-issued photo ID. This is a common survey deficiency.',
      unknown: 'Verify all employees have valid government-issued photo ID on file.',
    },
    stateNotes: [
      'Photo ID required for employment eligibility verification (I-9)',
      'Acceptable forms include driver\'s license, state ID, or passport',
      'Copy should be retained in personnel file',
      'Verify ID is current and matches employee information',
    ],
  },

  {
    id: 'ME-B3',
    itemId: 'B3',
    stateCode: 'ME',
    title: 'Professional License Verification',
    description: 'Maine requires verification of all professional licenses per 10-144 CMR Chapter 119',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'License Verification',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses with the Maine State Board of Nursing or other licensing boards before hire and during employment?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'Maine Office of Professional and Occupational Regulation (OPOR) maintains license records',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets Maine requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification to catch expired licenses.',
      nonCompliant: 'No license verification process creates significant risk. Implement verification with Maine licensing boards immediately.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Maine State Board of Nursing: (207) 287-1133',
      'Maine participates in the Nurse Licensure Compact (NLC) as of March 2024',
      'Physical Therapy licensing: 32 MRSA §3111 et seq.',
      'Verify licenses online at maine.gov/pfr/professionallicensing',
    ],
  },

  {
    id: 'ME-B4',
    itemId: 'B4',
    stateCode: 'ME',
    title: 'HIPAA/Confidentiality Agreement Signed',
    description: 'Maine requires signed HIPAA and confidentiality agreements for employees handling PHI',
    severity: 'high',
    regulationCode: '10-144-119.7.F.3',
    regulationName: 'Confidentiality',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your employees have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have signed HIPAA/confidentiality agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some employees are missing signed HIPAA/confidentiality agreements. This creates privacy risk.',
      nonCompliant: 'Personnel files are missing required HIPAA/confidentiality agreements. This is a serious compliance deficiency.',
      unknown: 'Verify all employees have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      'Section 7.F.3 requires written policy on security and confidentiality of patient records',
      'Policies must cover storage, use, removal, and release of patient information',
    ],
  },

  {
    id: 'ME-B5',
    itemId: 'B5',
    stateCode: 'ME',
    title: 'Signed Job Description on File',
    description: 'Maine requires signed job descriptions per 10-144 CMR Chapter 119',
    severity: 'medium',
    regulationCode: '10-144-119.1.26',
    regulationName: 'Job Descriptions',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your employees have a signed job description on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have signed job descriptions' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees have signed job descriptions on file.',
      atRisk: 'Some employees are missing signed job descriptions.',
      nonCompliant: 'Personnel files are missing required signed job descriptions.',
      unknown: 'Verify all employees have signed job descriptions on file.',
    },
    stateNotes: [
      'Per Section 1.26, job descriptions must contain qualifications, duties, competencies, responsibilities, and accountability',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'ME-C1',
    itemId: 'C1',
    stateCode: 'ME',
    title: 'Criminal Background Check (22 MRSA §1724)',
    description: 'Maine requires comprehensive background checks for all direct care staff per 22 MRSA §1724 and Chapter 1691',
    severity: 'high',
    regulationCode: '22-1724',
    regulationName: 'Background Check',
    agencyName: 'Maine Background Check Center',
    agencyAbbr: 'MBCC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://backgroundcheck.maine.gov',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed staff with patient contact, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'sbi', label: 'Maine State Bureau of Identification (SBI) check' },
          { value: 'fbi', label: 'FBI fingerprint-based background check' },
          { value: 'sex_offender', label: 'Sex offender registry check' },
          { value: 'abuse_registry', label: 'Abuse/neglect registry check' },
          { value: 'nurse_aide', label: 'CNA/Direct Care Worker Registry check (22 MRSA §1812-G)' },
        ],
        required: true,
        helpText: '22 MRSA §1724 requires criminal background checks before patient contact for Chapter 419 providers',
      },
      {
        id: 'q2',
        question: 'Do any staff begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all staff per 22 MRSA §1724.',
      atRisk: 'Background check documentation may be incomplete. Review all staff files immediately.',
      nonCompliant: 'Staff are working without proper background check clearance or are starting work before clearance is received. Employees working before background check clearance is a direct violation of 22 MRSA §1724. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Audit all staff files for background check documentation per 22 MRSA §1724.',
    },
    stateNotes: [
      'Use Maine Background Check Center portal: backgroundcheck.maine.gov',
      'Comprehensive background checks include SBI, FBI, abuse registries, sex offender registry',
      'Providers pay for background checks per 22 MRSA §1724',
      'Employment bans for disqualifying offenses per 22 MRSA §1812-G',
      'Chapter 1691 (Maine Background Check Center Act) governs the centralized system',
    ],
  },

  {
    id: 'ME-C2',
    itemId: 'C2',
    stateCode: 'ME',
    title: 'CNA/Direct Care Worker Registry Check',
    description: 'Maine requires verification with the Maine Registry of Certified Nursing Assistants and Direct Care Workers per 22 MRSA §1812-G',
    severity: 'high',
    regulationCode: '22-1812-G',
    regulationName: 'CNA Registry',
    agencyName: 'Maine Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify CNA/Direct Care Worker Registry status for all CNAs and direct care workers before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified before hire with no disqualifying notations' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CNA/Direct Care Worker Registry verification is conducted per 22 MRSA §1812-G.',
      atRisk: 'Registry verification is not consistent. Verify all CNAs/direct care workers before providing patient care.',
      nonCompliant: 'Registry verification is not being conducted. This is required by 22 MRSA §1812-G.',
      unknown: 'Verify registry check procedures are in place.',
    },
    stateNotes: [
      'Registry includes CNAs and direct care workers with notations for substantiated abuse/neglect',
      'Disqualifying notations prohibit employment in direct care positions',
      'Employers must verify no disqualifying notations before hire per Section 5',
      '10-144 CMR Chapter 128: Certified Nursing Assistant and Direct Care Worker Registry Rule',
    ],
  },

  {
    id: 'ME-C3',
    itemId: 'C3',
    stateCode: 'ME',
    title: 'OIG/SAM Exclusion Check',
    description: 'Federal exclusion list verification is required for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42-1001',
    regulationName: 'LEIE',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG List of Excluded Individuals/Entities (LEIE) and SAM for all staff?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly checks for all staff' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion checks are conducted monthly for all staff.',
      atRisk: 'Exclusion checks at hire only are insufficient. OIG recommends monthly verification.',
      nonCompliant: 'OIG/SAM exclusion checks are not being conducted. Employing excluded individuals can result in civil monetary penalties.',
      unknown: 'Verify OIG/SAM exclusion check procedures are in place.',
    },
    stateNotes: [
      'Maine Background Check Center may include exclusion database checks',
      'Medicare/Medicaid providers face significant penalties for employing excluded individuals',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'ME-D1',
    itemId: 'D1',
    stateCode: 'ME',
    title: 'TB Screening',
    description: 'Maine requires TB screening for all staff with patient contact',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'TB Screening',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff with patient contact have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed BEFORE patient contact',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening due dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT: Check for disqualifying conditions first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT: All positive conditions met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      // 3. AT_RISK: Partial compliance or missing tracking
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files and complete screenings.',
      nonCompliant: 'Staff are working without valid TB screening. This creates health risks.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Maine CDC provides TB screening guidance',
      'Contact Maine CDC TB Control: (207) 287-5500',
    ],
  },

  {
    id: 'ME-D2',
    itemId: 'D2',
    stateCode: 'ME',
    title: 'Infection Control Program',
    description: 'Maine requires a comprehensive infection control program',
    severity: 'high',
    regulationCode: '10-144-119',
    regulationName: 'Infection Control',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'waste', label: 'Medical waste disposal procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets requirements.',
      atRisk: 'Infection control program is missing some components. Review requirements.',
      nonCompliant: 'Infection control program is inadequate. Develop a comprehensive program immediately.',
      unknown: 'Review infection control program.',
    },
    stateNotes: [
      '10-144 CMR Chapter 119 requires infection control procedures',
      'Follow Maine CDC guidelines for healthcare workers',
      'Medicare-certified agencies must meet 42 CFR 484.70 requirements',
      'Document infection control training for all staff',
    ],
  },

  {
    id: 'ME-D3',
    itemId: 'D3',
    stateCode: 'ME',
    title: 'Hepatitis B Vaccination Offer',
    description: 'OSHA requires Hepatitis B vaccination be offered to at-risk employees',
    severity: 'medium',
    regulationCode: '29-1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'OSHA',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to employees with potential bloodborne pathogen exposure?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, offered to all at-risk employees' },
          { value: 'some', label: 'Offered to some employees' },
          { value: 'no', label: 'No / not offered' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document employee acceptance or declination of the Hepatitis B vaccine?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA requirements.',
      atRisk: 'Hepatitis B vaccination program may be incomplete. Document all offers and declinations.',
      nonCompliant: 'Hepatitis B vaccination is not being offered. This violates OSHA 29 CFR 1910.1030.',
      unknown: 'Verify Hepatitis B vaccination procedures are in place.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 requires employer to offer vaccination',
      'Vaccination must be offered within 10 days of at-risk assignment',
      'Declination form must be signed and kept in personnel file',
      'Employee may accept vaccination at any time at employer cost',
    ],
  },

  {
    id: 'ME-D4',
    itemId: 'D4',
    stateCode: 'ME',
    title: 'Workers\' Compensation Coverage',
    description: 'Maine requires workers\' compensation coverage for employees per 39-A MRSA',
    severity: 'high',
    regulationCode: '39-A',
    regulationName: 'Workers Compensation',
    agencyName: 'Maine Workers\' Compensation Board',
    agencyAbbr: 'WCB',
    agencyPhone: '(207) 287-3751',
    agencyUrl: 'https://www.maine.gov/wcb/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain current workers\' compensation insurance coverage?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is proof of workers\' compensation coverage current and on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers\' compensation coverage is current and documented.',
      atRisk: 'Workers\' compensation coverage exists but documentation may need updating.',
      nonCompliant: 'Workers\' compensation coverage is missing. This is required by 39-A MRSA.',
      unknown: 'Verify workers\' compensation coverage is in place.',
    },
    stateNotes: [
      'Maine Workers\' Compensation Board: (207) 287-3751',
      'Website: maine.gov/wcb/',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'ME-E1',
    itemId: 'E1',
    stateCode: 'ME',
    title: 'Orientation Training',
    description: 'Maine requires comprehensive orientation per 10-144 CMR Chapter 119, Section 1.49',
    severity: 'high',
    regulationCode: '10-144-119.1.49',
    regulationName: 'Orientation',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new staff complete the following BEFORE any patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'General agency orientation' },
          { value: 'policies', label: 'Review of agency policies and procedures' },
          { value: 'safety', label: 'Safety and emergency procedures training' },
          { value: 'infection', label: 'Infection control training' },
          { value: 'hipaa', label: 'HIPAA and confidentiality training' },
          { value: 'abuse', label: 'Abuse recognition and reporting (22 MRSA §3477)' },
        ],
        required: true,
        helpText: 'Section 1.49 defines Staff Orientation Plan requirements',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets Maine requirements.',
      atRisk: 'Orientation training may be incomplete. Review requirements.',
      nonCompliant: 'Staff are providing services without required orientation training.',
      unknown: 'Review orientation training program against requirements.',
    },
    stateNotes: [
      'Per Section 1.49: orientation must cover duties, responsibilities, and personnel policies',
    ],
  },

  {
    id: 'ME-E2',
    itemId: 'E2',
    stateCode: 'ME',
    title: 'In-Service Training Requirements',
    description: 'Maine requires ongoing in-service training per 10-144 CMR Chapter 119, Section 1.48',
    severity: 'medium',
    regulationCode: '10-144-119.1.48',
    regulationName: 'In-Service Training',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ongoing in-service training for clinical staff?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented in-service training (annual plan)' },
          { value: 'occasional', label: 'Occasional training, not consistent' },
          { value: 'no', label: 'No ongoing training program' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-service training program meets Maine requirements.',
      atRisk: 'In-service training is not consistent. Implement regular training schedule.',
      nonCompliant: 'No ongoing in-service training program exists.',
      unknown: 'Review in-service training program.',
    },
    stateNotes: [
      'Per Section 1.48: Staff Education Plan must be developed at least annually',
      'Plan must describe coordinated staff education including inservice programs and on-the-job training',
    ],
  },

  {
    id: 'ME-E3',
    itemId: 'E3',
    stateCode: 'ME',
    title: 'Abuse Reporting Training (22 MRSA §3477)',
    description: 'Maine requires mandatory reporting training for vulnerable adult abuse',
    severity: 'high',
    regulationCode: '22-3477',
    regulationName: 'Abuse Reporting',
    agencyName: 'Maine Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '1-800-624-8404',
    agencyUrl: 'https://www.maine.gov/dhhs/oads/get-support/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on identifying and reporting suspected abuse, neglect, or exploitation?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to Maine Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, APS hotline (1-800-624-8404) is documented and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff are properly trained on mandatory abuse reporting per 22 MRSA §3477.',
      atRisk: 'Abuse reporting training may be insufficient. Ensure ongoing training and documented procedures.',
      nonCompliant: 'Failure to train on mandatory abuse reporting violates 22 MRSA §3477. Staff who fail to report can face criminal penalties.',
      unknown: 'Verify abuse reporting training meets 22 MRSA §3477 requirements.',
    },
    stateNotes: [
      'Home health workers are mandatory reporters under 22 MRSA §3477(1)(B)',
      'Reports must be made IMMEDIATELY by telephone to APS: 1-800-624-8404',
      'Written report within 48 hours if requested by department',
      'Reporters are immune from civil liability when reporting in good faith (22 MRSA §3479-A)',
      'Failure to report is a criminal offense',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'ME-F1',
    itemId: 'F1',
    stateCode: 'ME',
    title: 'Plan of Care (Section 7.E)',
    description: 'Maine requires comprehensive plans of care per 10-144 CMR Chapter 119, Section 7.E',
    severity: 'high',
    regulationCode: '10-144-119.7.E',
    regulationName: 'Plan of Care',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient initiated within 3 business days of admission?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 3 business days' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per Section 7.E.3, plans of care must be initiated within 3 business days after admission',
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all Maine-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'assessment', label: 'RN/professional assessment at initial visit' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'patient_consult', label: 'Patient/family consultation documented' },
          { value: 'physician_orders', label: 'Physician orders (if required by payer)' },
          { value: 'review_90', label: 'Review at least every 90 days (if physician supervision)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are initiated timely and include all required elements per Section 7.E.',
      atRisk: 'Plans of care may be incomplete or delayed. Review Section 7.E requirements.',
      nonCompliant: 'Services are being provided without proper plans of care. This violates Section 7.E.',
      unknown: 'Review plan of care process against Section 7.E requirements.',
    },
    stateNotes: [
      'Section 7.E.1: Plans for nursing patients must be written by RN who made initial visit',
      'Section 7.E.2: Plans for therapy-only patients developed by licensed professional',
      'Section 7.E.3: Plan must be initiated within 3 business days after admission',
      'Visit ranges permitted but may not begin with zero (except PRN orders)',
    ],
  },

  {
    id: 'ME-F2',
    itemId: 'F2',
    stateCode: 'ME',
    title: 'Clinical Supervision (Section 7.D)',
    description: 'Maine requires appropriate clinical supervision per 10-144 CMR Chapter 119, Section 7.D',
    severity: 'high',
    regulationCode: '10-144-119.7.D',
    regulationName: 'Supervision',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a qualified clinical supervisor (RN) who oversees patient care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do supervisory visits occur for home health aide/CNA services?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 calendar days or more frequently (meets requirement)' },
          { value: 'monthly', label: 'Every 30 days (supervisory visit to residence)' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'rarely', label: 'Rarely / as needed' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Section 7.D.4: RN supervision not less than every 14 calendar days; Section 7.D.4: Home visit every 30 days',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'every_14', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'rarely'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical supervision meets Maine Section 7.D requirements.',
      atRisk: 'Supervision frequency may not meet all requirements. Review Section 7.D standards.',
      nonCompliant: 'Clinical supervision is inadequate or missing. This violates Section 7.D.',
      unknown: 'Verify supervision practices against Section 7.D.',
    },
    stateNotes: [
      'Section 7.D.4: HHA/CNA supervised by RN not less than every 14 calendar days',
      'RN must be readily available by telephone when not providing direct supervision',
      'Supervisory visit to patient residence at least every 30 days',
      'Alternate supervisory visits may be by telephone',
      'Section 7.D.6: 1 RN for every 25 FTE aides/CNAs',
    ],
  },

  {
    id: 'ME-F3',
    itemId: 'F3',
    stateCode: 'ME',
    title: 'Patient Records (Section 7.F)',
    description: 'Maine requires complete clinical documentation per 10-144 CMR Chapter 119, Section 7.F',
    severity: 'high',
    regulationCode: '10-144-119.7.F',
    regulationName: 'Clinical Records',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation practices does your agency follow? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'visit_notes', label: 'Clinical notes written same day and incorporated weekly' },
          { value: 'signatures', label: 'All entries signed and dated' },
          { value: 'plan_of_care', label: 'Plan of care in each record' },
          { value: 'medication_list', label: 'Medication list with start/stop dates' },
          { value: 'assessments', label: 'Initial and periodic assessments documented' },
          { value: 'discharge_summary', label: 'Discharge summary completed' },
          { value: 'hotline_notice', label: 'Documentation that hotline number was provided' },
        ],
        required: true,
        helpText: 'Section 7.F.1 lists required elements of patient records',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation practices meet Maine Section 7.F requirements.',
      atRisk: 'Documentation practices need improvement. Review Section 7.F requirements.',
      nonCompliant: 'Documentation practices are inadequate. Implement proper procedures per Section 7.F.',
      unknown: 'Review documentation against Section 7.F requirements.',
    },
    stateNotes: [
      'Section 7.F.1.h: Clinical notes written same day, incorporated weekly',
      'Section 7.F.1.r: Must document patient received hotline number',
      'Section 7.F.2: Records retained minimum 5 years beyond last service date',
      'Physician rubber stamp permitted with signed attestation on file (Section 7.F.1.s)',
    ],
  },

  {
    id: 'ME-F4',
    itemId: 'F4',
    stateCode: 'ME',
    title: 'Patient Rights (Section 7.G.4)',
    description: 'Maine requires patient rights be provided per 10-144 CMR Chapter 119, Section 7.G.4',
    severity: 'high',
    regulationCode: '10-144-119.7.G.4',
    regulationName: 'Patient Rights',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients before initiation of treatment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide the following information in writing? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'rights_notice', label: 'Written notice of patient rights' },
          { value: 'ombudsman', label: 'Long Term Care Ombudsman Program toll-free number' },
          { value: 'hotline', label: 'Home Health Care Services Hotline number' },
          { value: 'payment', label: 'Payment expectations before care begins' },
          { value: 'complaint', label: 'Right to file complaints with DLC, BEAS, or Ombudsman' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided per Section 7.G.4 requirements.',
      atRisk: 'Some required patient rights elements may be missing.',
      nonCompliant: 'Patient rights are not being provided. This violates Section 7.G.4.',
      unknown: 'Verify patient rights process meets Section 7.G.4 requirements.',
    },
    stateNotes: [
      'Section 7.G.4.a: Written rights notice before care or at initial evaluation',
      'Section 7.G.4.a.2: Must provide Long Term Care Ombudsman Program number',
      'Section 7.G.4.f: Must advise of Home Health Care Services Hotline',
      'Section 7.G.4.g: Patients can file complaints with DLC, BEAS, or Ombudsman',
    ],
  },

  {
    id: 'ME-F5',
    itemId: 'F5',
    stateCode: 'ME',
    title: 'Transfer and Discharge (Section 7.G)',
    description: 'Maine requires proper transfer/discharge procedures per 10-144 CMR Chapter 119, Section 7.G',
    severity: 'medium',
    regulationCode: '10-144-119.7.G',
    regulationName: 'Transfer/Discharge',
    agencyName: 'Maine Department of Health and Human Services - Division of Licensing and Certification',
    agencyAbbr: 'DHHS-DLC',
    agencyPhone: '(207) 287-9302',
    agencyUrl: 'https://www.maine.gov/dhhs/dlc/licensing-certification/medical-facilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written criteria for transfer, referral, and discharge?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide written discharge notice at least 14 days before services end?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, with appeal rights notification' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Section 7.G.2 requires 14-day written notice with appeal rights',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Transfer and discharge procedures meet Section 7.G requirements.',
      atRisk: 'Transfer/discharge procedures exist but 14-day notice may not be consistent.',
      nonCompliant: 'No transfer/discharge procedures in place. This violates Section 7.G.',
      unknown: 'Verify transfer/discharge procedures meet Section 7.G requirements.',
    },
    stateNotes: [
      'Section 7.G.1: Written criteria must exist for transfer, referral, and discharge',
      'Section 7.G.2: 14-day written notice required with appeal rights (exceptions for improvement or safety)',
      'Section 7.G.3: Documentation of criteria in clinical record required',
    ],
  },
];

export default MAINE_QUESTIONS;